import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useProviderCategories } from "@/hooks/useProviders";
import { SuburbSelect } from "./SuburbSelect";

const ProviderForm = ({ onSuccess, onCancel, initialData }) => {
    const { user } = useAuth();
    const { categories, loading: categoriesLoading } = useProviderCategories();
    const [name, setName] = useState("");
    const [selectedCategoryId, setSelectedCategoryId] = useState("");
    const [phone, setPhone] = useState("");
    const [phone2, setPhone2] = useState("");
    const [email, setEmail] = useState("");
    const [serviceArea, setServiceArea] = useState("");
    const [loading, setLoading] = useState(false);

    const isEditing = !!initialData;

    // Populate fields when editing
    useEffect(() => {
        if (initialData && categories.length > 0) {
            setName(initialData.name || "");
            setPhone(initialData.phone || "");
            setPhone2(initialData.phone_2 || "");
            setEmail(initialData.email || "");
            setServiceArea(initialData.service_area || "");

            // Resolve category_id from name if not directly available
            if (initialData.category_id) {
                setSelectedCategoryId(initialData.category_id);
            } else if (initialData.category) {
                const cat = categories.find(c => c.name === initialData.category);
                if (cat) setSelectedCategoryId(cat.id);
            }
        }
    }, [initialData, categories]);

    const isSaveDisabled = !name || !selectedCategoryId || !serviceArea || loading;

    const handleSave = async () => {
        if (!name || !selectedCategoryId || !user) return;

        // Validation: Suburb is required (the label says "Suburb *" — this used to
        // not be enforced, so providers could be approved with no service area and
        // then never surface in any suburb filter on the public directory)
        if (!serviceArea) {
            toast.error("Please select the suburb this provider operates in");
            return;
        }

        // Validation: At least one contact method
        if (!phone && !phone2 && !email) {
            toast.error("Please provide at least one contact method (Phone or Email)");
            return;
        }

        const categoryObj = categories.find(c => c.id === selectedCategoryId);
        if (!categoryObj) return;

        setLoading(true);

        const suburbsArray = serviceArea
            ? serviceArea.split(",").map(s => s.trim()).filter(Boolean)
            : [];

        const providerPayload = {
            name,
            category: categoryObj.name,
            category_id: categoryObj.id,
            phone: phone || null,
            phone_2: phone2 || null,
            email: email || null,
            service_area: serviceArea || null,
            service_suburbs: suburbsArray,
        };

        if (isEditing) {
            // Update existing provider
            const { data, error } = await supabase
                .from("providers")
                .update(providerPayload)
                .eq("id", initialData.id)
                .select()
                .single();

            setLoading(false);

            if (error) {
                toast.error("Failed to update provider: " + error.message);
            } else {
                toast.success("Provider updated successfully.");
                onSuccess?.(data);
            }
        } else {
            // Insert new provider
            const { data, error } = await supabase.from("providers").insert({
                ...providerPayload,
                email_2: null,
                is_verified: false,
                is_featured: false,
                added_by_user_id: user.id,
            }).select().single();

            setLoading(false);

            if (error) {
                toast.error("Failed to add provider: " + error.message);
            } else {
                toast.success("Provider added! They will be visible in the directory after verification.");
                onSuccess?.(data);
            }
        }
    };

    return (
        <div className="space-y-4 py-2">
            <div className="space-y-2">
                <Label htmlFor="provider-name">Provider Name *</Label>
                <Input
                    id="provider-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g., Joe's Plumbing"
                    className="rounded-xl"
                />
            </div>

            <div className="space-y-2">
                <Label htmlFor="provider-category">Category *</Label>
                <Select value={selectedCategoryId} onValueChange={setSelectedCategoryId}>
                    <SelectTrigger id="provider-category" className="rounded-xl">
                        <SelectValue placeholder={categoriesLoading ? "Loading..." : "Select category"} />
                    </SelectTrigger>
                    <SelectContent>
                        {categories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                                {cat.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                    <Label htmlFor="provider-phone">Phone 1</Label>
                    <Input
                        id="provider-phone"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="Primary number"
                        className="rounded-xl"
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="provider-phone-2">Phone 2 (Optional)</Label>
                    <Input
                        id="provider-phone-2"
                        value={phone2}
                        onChange={(e) => setPhone2(e.target.value)}
                        placeholder="Secondary number"
                        className="rounded-xl"
                    />
                </div>
            </div>

            <div className="space-y-2">
                <Label htmlFor="provider-email">Email</Label>
                <Input
                    id="provider-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Provider email"
                    className="rounded-xl"
                />
            </div>

            <div className="space-y-2">
                <Label htmlFor="provider-suburb">Suburb *</Label>
                <SuburbSelect
                    value={serviceArea}
                    onSelect={setServiceArea}
                    placeholder="Select suburb"
                />
                <p className="text-[10px] text-muted-foreground ml-1">
                    Select the main suburb where this provider operates.
                </p>
            </div>

            <div className="flex gap-3 pt-4">
                {onCancel && (
                    <Button variant="outline" onClick={onCancel} className="flex-1 rounded-xl">
                        Cancel
                    </Button>
                )}
                <Button
                    onClick={handleSave}
                    className="flex-1 rounded-xl"
                    disabled={isSaveDisabled}
                >
                    {loading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        isEditing ? "Save Changes" : "Add Provider"
                    )}
                </Button>
            </div>
        </div>
    );
};

export default ProviderForm;
